import { getAuth } from '@garner-health/components-auth';
import { writeLogs } from '@garner-health/lib-ui-real-user-monitoring';
import createModuleLogger, { Bindings, createLogger, Logger } from '@garner-health/logging';
import constants from 'app-constants';

import { bowserParser } from './browser';

const platform = bowserParser.getPlatform();

const sessionId = crypto.randomUUID();
const log = createLogger({
  logLevel: constants.logLevel,
  logDestination: {
    // Calling the API host directly leaves one CloudFront hop out of X-Forwarded-For, so the
    // authorization layer reads the staff member's real IP instead of a CloudFront edge address.
    // Local keeps `fullApiBaseUrl` relative, so this still resolves to the `/api` dev proxy.
    path: `${constants.fullApiBaseUrl}/logs`,
    async lazyBindings(): Promise<Bindings> {
      try {
        const auth = getAuth();
        const claims = await auth.getTokenClaims();
        return {
          careNavigatorId: claims['custom:care_navigator_id'],
          clientId: claims['custom:client_id'],
        };
      } catch {
        // Can't log here because it would create a potentially infinite loop of logging errors
        return {};
      }
    },
  },
}).child({
  component: 'datapro-ui',
  strSessionId: sessionId,
  strBrowser: bowserParser.getBrowserName(),
  strBrowserVersion: bowserParser.getBrowserVersion(),
  strOperatingSystem: bowserParser.getOSName(),
  strPlatformVendor: platform.vendor,
  strPlatformType: platform.type,
  strUserAgent: bowserParser.getUA(),
  strAppVersion: GARNER_VERSION,
  strBuildNumber: GARNER_BUILD_NUMBER,
  strCommitSha: GARNER_COMMIT_SHA,
});

if (!['development', 'test'].includes(process.env.NODE_ENV!)) {
  const externalLog = log.child({ module: 'datapro-ui-external' });
  Object.assign(console, {
    log: (...args: unknown[]) => externalLog.info({ context: args }),
    info: (...args: unknown[]) => externalLog.info({ context: args }),
    warn: (...args: unknown[]) => externalLog.warn({ context: args }),
    error: (...args: unknown[]) => externalLog.error({ context: args }),
    fatal: (...args: unknown[]) => externalLog.fatal({ context: args }),
    trace: (...args: unknown[]) => externalLog.trace({ context: args }),
    debug: (...args: unknown[]) => externalLog.debug({ context: args }),
  });
}

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

const PROXIED_LOG_LEVELS = new Set<LogLevel>(['trace', 'debug', 'info', 'warn', 'error', 'fatal']);

/**
 * Returns a module logger whose log calls are also forwarded to Datadog Logs, with `error`/`fatal`
 * additionally surfaced as RUM errors (see {@link writeLogs}). Only the log-level methods are
 * wrapped — every other member passes through to the underlying logger, so a logger created via
 * `.child()` is NOT forwarded to Datadog (there are no such callers today). Datadog forwarding is a
 * safe no-op until `initDatadog()` has run.
 */
export default function logger(module: string, bindings?: Omit<Bindings, 'module'>): Logger {
  const moduleLogger = createModuleLogger(module, { log }, bindings as Bindings & { module: never });

  return new Proxy(moduleLogger, {
    get(target, property, receiver) {
      if (typeof property === 'string' && PROXIED_LOG_LEVELS.has(property as LogLevel)) {
        return (arg1: unknown, arg2?: unknown) => writeLogs(target, property as LogLevel, arg1, arg2);
      }
      return Reflect.get(target, property, receiver);
    },
  });
}
